import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Link } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { configData } from '../config';
import Loading from './Loading';
import { visualPageTypeLabel } from '../constants/visualPageTypes';
import './classes/ManageBlogPost.css';

// Slim manage-list for CMS Visual Pages. Mirrors the ManageCoursePages pattern
// (MUI DataGrid, server-side pagination shape) but points at the pending
// backend endpoint `pages/listvisualpages`. Falls back to an empty grid with
// a small notice while that endpoint is not yet deployed.
// Columns are built via a factory so the Actions column can close over the
// component's `onDelete` handler (which needs access to state setters). The
// title/URL/type/status columns are unchanged.
const buildColumns = (onDelete) => [
  {
    field: 'title',
    headerName: 'Title',
    cellClassName: 'title_column',
    headerClassName: 'title_header',
    minWidth: 200,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Link
        href={'/' + (params.row.url_slug || '')}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#2C99D2' }}
      >
        {params.row.title}
      </Link>
    ),
  },
  {
    field: 'url_slug',
    headerName: 'URL',
    minWidth: 200,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (params) => <span style={{ color: '#6b7280' }}>/{params.row.url_slug}</span>,
  },
  {
    field: 'page_type',
    headerName: 'Page Type',
    minWidth: 140,
    sortable: false,
    filterable: false,
    // Renders the friendly label (e.g. "Success Story") from the stored
    // value (e.g. "success_story"). Unknown / legacy blanks show a dash.
    renderCell: (params) => (
      <span style={{ color: params.row.page_type ? '#1f2937' : '#9ca3af' }}>
        {params.row.page_type ? visualPageTypeLabel(params.row.page_type) : '—'}
      </span>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <span style={{ color: Number(params.row.status) === 1 ? '#059669' : '#b45309' }}>
        {Number(params.row.status) === 1 ? 'Published' : 'Draft'}
      </span>
    ),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellClassName: 'operations_column',
    headerClassName: 'operations_header',
    sortable: false,
    filterable: false,
    minWidth: 180,
    renderCell: (params) => (
      <>
        <Link href={'/' + (params.row.url_slug || '')} rel="noopener noreferrer" style={{ color: '#2C99D2' }}>
          View
        </Link>
        &nbsp;&nbsp;
        <Link href={'/' + (params.row.url_slug || '') + '/edit'} rel="noopener noreferrer" style={{ color: '#2C99D2' }}>
          Edit
        </Link>
        &nbsp;&nbsp;
        <Link
          component="button"
          type="button"
          onClick={() => onDelete(params.row)}
          style={{ color: '#dc2626', cursor: 'pointer' }}
        >
          Delete
        </Link>
      </>
    ),
  },
];

const ManageVisualPages = () => {
  const app_url = configData.APP_URL;
  const location = useLocation();
  const successCreateMessage = location.state?.message;

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({ title: '' });

  useEffect(() => {
    if (successCreateMessage) {
      setSuccessMessage(successCreateMessage);
      window.history.replaceState({}, '');
    }
  }, [successCreateMessage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        const response = await fetch(app_url + 'pages/listvisualpages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sortModel,
            filterModel,
            formData,
          }),
        });
        if (!response.ok) {
          throw new Error('Backend not available (HTTP ' + response.status + ')');
        }
        const data = await response.json();
        if (cancelled) return;
        const list = (data && data.data && data.data.rows) || [];
        setRows(list);
        setRowCount((data && data.data && data.data.totalRowCount) || list.length);
      } catch (err) {
        if (cancelled) return;
        setRows([]);
        setRowCount(0);
        // Surface the ACTUAL failure reason instead of a single generic line,
        // so a missing endpoint (404), auth failure (401/403), server error
        // (500) or a down proxy target (network error) can be told apart.
        const reason = (err && err.message) || 'request failed';
        setErrorMessage(
          'Could not load visual pages from `pages/listvisualpages` (' + reason + '). ' +
          'In local dev the proxy routes this to the local backend — make sure it is ' +
          'running and implements the action; otherwise the endpoint may not be deployed yet.'
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [app_url, paginationModel, sortModel, filterModel]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    // Trigger a refetch by nudging the pagination model.
    setPaginationModel((m) => ({ ...m }));
  };

  // Delete a single visual page. Confirms first (Cancel => no-op), then calls
  // the backend which permanently removes that one row (keyed by nid), so a
  // refresh cannot bring it back. On success the row is dropped from the grid;
  // backend/network errors surface in the existing error banner and the list
  // is left untouched.
  const handleDelete = useCallback(
    async (row) => {
      const nid = row && row.nid;
      if (!nid) return;
      const label = (row.title && row.title.trim()) || row.url_slug || nid;
      const confirmed = window.confirm(
        `Delete the visual page "${label}"? This permanently removes it and cannot be undone.`
      );
      if (!confirmed) return;
      try {
        setErrorMessage('');
        setSuccessMessage('');
        const response = await fetch(app_url + 'pages/deletevisualpage', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ nid }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || (data && data.code && data.code !== 0)) {
          throw new Error((data && data.message) || 'HTTP ' + response.status);
        }
        // Remove only the deleted page from the grid.
        setRows((prev) => prev.filter((r) => r.nid !== nid));
        setRowCount((c) => Math.max(0, c - 1));
        setSuccessMessage(`Visual page "${label}" deleted.`);
      } catch (err) {
        setErrorMessage(
          'Delete failed: ' + (err && err.message ? err.message : 'unknown error') + '. No page was deleted.'
        );
      }
    },
    [app_url]
  );

  const columns = useMemo(() => buildColumns(handleDelete), [handleDelete]);

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <div id="main">
          <div className="left"></div>
          <div className="center left-center">
            <div className="manage-blog-container">
              <Helmet>
                <title>Manage Visual Pages</title>
              </Helmet>
              {successMessage && (
                <div className="set-message-done">
                  <span className="cart-message-icon message-icon">
                    <i className="fa fa-check-circle"></i>
                  </span>{' '}
                  {successMessage}
                </div>
              )}
              {errorMessage && (
                <div
                  style={{
                    background: '#fef3c7',
                    border: '1px solid #fde68a',
                    color: '#92400e',
                    padding: '10px 12px',
                    borderRadius: 6,
                    marginBottom: 12,
                    fontSize: 13,
                  }}
                >
                  {errorMessage}
                </div>
              )}
              <div>
                <p>
                  <a href="/node/add/visual-page">Create New Visual Page</a>
                </p>
                <form onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="edit-title">Title</label>
                    <div>
                      <input
                        type="text"
                        id="edit-title"
                        name="title"
                        onChange={handleChange}
                        value={formData.title}
                        size={30}
                        maxLength={128}
                      />
                    </div>
                    <div>
                      <input type="submit" value="Apply" className="form-submit" />
                    </div>
                  </div>
                </form>
              </div>
              <div className="manage-blog-view-content">
                <div style={{ width: '100%' }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    rowCount={rowCount}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    pageSizeOptions={[5, 10, 25, 50]}
                    getRowId={(r) => r.nid || crypto.randomUUID()}
                    getRowHeight={() => 'auto'}
                    disableColumnFilter
                    disableColumnResize={true}
                    autoHeight
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageVisualPages;
